import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { localProducts } from '../../data/localDb';
import SVGIcon from '../SVGIcon/SVGIcon';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import './ShopPage.css';

export default function ShopPage({
  onProductClick,
  onAddToCart,
  onAddToWishlist,
  wishlistItems = [],
  convertPrice,
  selectedCurr = 'INR',
  onBack
}) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Filters State
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'All');
  const [priceMax, setPriceMax] = useState(2000);
  const [selectedRatings, setSelectedRatings] = useState([]); // [4, 3, etc]
  const [selectedPromos, setSelectedPromos] = useState({
    inStock: false,
    bestSeller: false,
    flagship: false,
    onSale: false
  });
  const [sortBy, setSortBy] = useState('default');
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  // Products Data State
  const [products, setProducts] = useState(localProducts);
  const [loading, setLoading] = useState(false);

  // Sync search query parameter from URL
  useEffect(() => {
    const searchFromUrl = searchParams.get('search');
    if (searchFromUrl !== null) {
      setSearchQuery(searchFromUrl);
    }
    const catFromUrl = searchParams.get('category');
    if (catFromUrl !== null) {
      setSelectedCategory(catFromUrl);
    }
  }, [searchParams]);

  // Fetch products from backend API if available, fallback to localProducts
  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    axios.get(`${API_BASE_URL}/products?t=${Date.now()}`)
      .then(res => {
        if (isMounted && res.data && res.data.products && res.data.products.length > 0) {
          // Merge API products with local DB extra metadata if available
          const merged = res.data.products.map(apiProd => {
            const matchedLocal = localProducts.find(lp => lp.id === apiProd.id || lp.slug === apiProd.slug);
            return {
              ...apiProd,
              category_name: apiProd.category_name || (matchedLocal ? matchedLocal.category_name : 'Bio Fertilizers'),
              images: apiProd.images && apiProd.images.length > 0 ? apiProd.images : (matchedLocal ? matchedLocal.images : ['/products/bio-orbi-super-novel.png']),
              starting_price: apiProd.starting_price || apiProd.price || (matchedLocal ? matchedLocal.starting_price : '190.00'),
              compare_at_price: apiProd.compare_at_price || (matchedLocal ? matchedLocal.compare_at_price : '300.00'),
              rating: apiProd.rating || (matchedLocal ? matchedLocal.rating : '4.5')
            };
          });
          setProducts(merged);
        } else if (isMounted) {
          setProducts(localProducts);
        }
      })
      .catch(() => {
        if (isMounted) setProducts(localProducts);
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });
    return () => { isMounted = false; };
  }, []);

  // Price conversion helper
  const renderPrice = (priceVal) => {
    if (convertPrice) {
      return convertPrice(priceVal, selectedCurr);
    }
    const num = parseFloat(priceVal);
    if (isNaN(num)) return priceVal;
    return `₹${num.toFixed(2)}`;
  };

  // Derive available categories dynamically
  const categories = useMemo(() => {
    const set = new Set(['All']);
    products.forEach(p => {
      const cat = p.category_name || p.category;
      if (cat) set.add(cat);
    });
    return Array.from(set);
  }, [products]);

  // Filtering Logic
  const filteredProducts = useMemo(() => {
    return products.filter(p => {
      // 1. Search Query Filter
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const titleMatch = (p.title || '').toLowerCase().includes(q);
        const descMatch = (p.short_description || p.description || '').toLowerCase().includes(q);
        const catMatch = (p.category_name || p.category || '').toLowerCase().includes(q);
        if (!titleMatch && !descMatch && !catMatch) return false;
      }

      // 2. Category Filter
      if (selectedCategory !== 'All') {
        const cat = p.category_name || p.category || '';
        if (cat.toLowerCase() !== selectedCategory.toLowerCase()) return false;
      }

      // 3. Price Filter
      const price = parseFloat(p.starting_price || p.price || 0);
      if (price > priceMax) return false;

      // 4. Rating Filter
      if (selectedRatings.length > 0) {
        const r = parseFloat(p.rating || 4.0);
        const matchesRating = selectedRatings.some(minR => r >= minR);
        if (!matchesRating) return false;
      }

      // 5. Promo/Availability Filters
      if (selectedPromos.inStock && p.status === 'out_of_stock') return false;
      if (selectedPromos.flagship && !p.slug?.includes('super-novel') && !p.title?.toLowerCase().includes('super novel')) return false;
      if (selectedPromos.bestSeller && parseFloat(p.rating || 4.0) < 4.5) return false;
      if (selectedPromos.onSale) {
        const curPrice = parseFloat(p.starting_price || p.price || 0);
        const compPrice = parseFloat(p.compare_at_price || 0);
        if (compPrice <= curPrice) return false;
      }

      return true;
    });
  }, [products, searchQuery, selectedCategory, priceMax, selectedRatings, selectedPromos]);

  // Sorting Logic
  const sortedProducts = useMemo(() => {
    const list = [...filteredProducts];
    if (sortBy === 'price-low') {
      list.sort((a, b) => parseFloat(a.starting_price || a.price || 0) - parseFloat(b.starting_price || b.price || 0));
    } else if (sortBy === 'price-high') {
      list.sort((a, b) => parseFloat(b.starting_price || b.price || 0) - parseFloat(a.starting_price || a.price || 0));
    } else if (sortBy === 'rating') {
      list.sort((a, b) => parseFloat(b.rating || 0) - parseFloat(a.rating || 0));
    } else if (sortBy === 'newest') {
      list.sort((a, b) => (b.id || 0) - (a.id || 0));
    }
    return list;
  }, [filteredProducts, sortBy]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedCategory, priceMax, selectedRatings, selectedPromos, sortBy]);

  // Paginated slice
  const totalPages = Math.ceil(sortedProducts.length / itemsPerPage);
  const paginatedProducts = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return sortedProducts.slice(start, start + itemsPerPage);
  }, [sortedProducts, currentPage]);

  const handleProductClickInternal = (prod) => {
    if (onProductClick) {
      onProductClick(prod);
    } else {
      const slugVal = prod.slug || prod.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      navigate(`/product/${slugVal}`);
      window.scrollTo(0, 0);
    }
  };

  const handleWishlistToggle = (prod, e) => {
    e.stopPropagation();
    if (onAddToWishlist) {
      onAddToWishlist(prod);
    }
  };

  const handleRatingCheckboxChange = (ratingVal) => {
    setSelectedRatings(prev =>
      prev.includes(ratingVal) ? prev.filter(r => r !== ratingVal) : [...prev, ratingVal]
    );
  };

  const handlePromoCheckboxChange = (key) => {
    setSelectedPromos(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const clearAllFilters = () => {
    setSearchQuery('');
    setSelectedCategory('All');
    setPriceMax(2000);
    setSelectedRatings([]);
    setSelectedPromos({ inStock: false, bestSeller: false, flagship: false, onSale: false });
    setSortBy('default');
    setSearchParams({});
  };

  const activeFilterCount = (searchQuery ? 1 : 0) +
    (selectedCategory !== 'All' ? 1 : 0) +
    (priceMax < 2000 ? 1 : 0) +
    selectedRatings.length +
    Object.values(selectedPromos).filter(Boolean).length;

  return (
    <div className="shop-page">
      {/* Breadcrumb Bar */}
      <div className="shop-breadcrumb">
        <div className="container shop-breadcrumb-inner">
          <span className="bc-home" onClick={() => { if (onBack) onBack(); else navigate('/'); }}>Home</span>
          <span className="bc-sep">/</span>
          <span className="bc-current">Shop Catalog</span>
        </div>
      </div>

      {/* Hero Banner Header */}
      <div className="shop-hero">
        <div className="container shop-hero-content">
          <h1>Our Organic Products</h1>
          <p>
            Explore our complete line of 100% NPOP-certified bio-fertilizers, soil health restorers, and plant growth supplements engineered for maximum crop yield.
          </p>
        </div>
      </div>

      {/* Main Layout Container */}
      <div className="shop-layout">
        {/* Mobile Filter Toggle */}
        <button
          className="mobile-filter-toggle"
          onClick={() => setMobileFilterOpen(!mobileFilterOpen)}
        >
          <SVGIcon name="filter" size={18} stroke="#ffffff" />
          <span>{mobileFilterOpen ? 'Hide Filters' : 'Filters & Categories'}</span>
          {activeFilterCount > 0 && (
            <span style={{
              background: '#fff',
              color: '#2e7d32',
              borderRadius: '50%',
              width: '20px',
              height: '20px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '11px',
              fontWeight: 700,
              marginLeft: '4px'
            }}>{activeFilterCount}</span>
          )}
        </button>

        {/* Left Filter Sidebar */}
        <aside className={`shop-sidebar ${mobileFilterOpen ? 'open' : ''}`}>
          {/* Category Filter */}
          <div className="sidebar-section">
            <div className="sidebar-label">Categories</div>
            <ul className="sidebar-category-list">
              {categories.map((cat) => (
                <li
                  key={cat}
                  className={`sidebar-category-item ${selectedCategory.toLowerCase() === cat.toLowerCase() ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(cat)}
                >
                  <span className="cat-dot"></span>
                  <span>{cat}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Price Range Filter */}
          <div className="sidebar-section">
            <div className="sidebar-label">Filter by Price</div>
            <div className="price-range-display">
              <span>Max: {renderPrice(priceMax)}</span>
            </div>
            <div className="price-slider-wrap">
              <input
                type="range"
                className="price-range-input"
                min="100"
                max="2000"
                step="50"
                value={priceMax}
                onChange={(e) => setPriceMax(Number(e.target.value))}
              />
            </div>
            <div className="price-slider-labels">
              <span>₹100</span>
              <span>₹2,000</span>
            </div>
          </div>

          {/* Rating Filter */}
          <div className="sidebar-section">
            <div className="sidebar-label">Customer Rating</div>
            <div className="sidebar-ratings-list">
              {[4.5, 4.0, 3.5].map((ratingVal) => (
                <label key={ratingVal} className="sidebar-rating-row">
                  <input
                    type="checkbox"
                    checked={selectedRatings.includes(ratingVal)}
                    onChange={() => handleRatingCheckboxChange(ratingVal)}
                  />
                  <div className="star-rating">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <span key={star} className={`star ${star <= ratingVal ? 'filled' : ''}`}>★</span>
                    ))}
                  </div>
                  <span className="rating-label">&amp; up ({ratingVal}★)</span>
                </label>
              ))}
            </div>
          </div>

          {/* Promotions & Availability Filter */}
          <div className="sidebar-section">
            <div className="sidebar-label">Tag &amp; Availability</div>

            <label className="sidebar-checkbox-row">
              <input
                type="checkbox"
                checked={selectedPromos.inStock}
                onChange={() => handlePromoCheckboxChange('inStock')}
              />
              <span className="promo-dot instock"></span>
              <span>In Stock Only</span>
            </label>

            <label className="sidebar-checkbox-row">
              <input
                type="checkbox"
                checked={selectedPromos.flagship}
                onChange={() => handlePromoCheckboxChange('flagship')}
              />
              <span className="promo-dot new"></span>
              <span>Govt. Patent / Flagship</span>
            </label>

            <label className="sidebar-checkbox-row">
              <input
                type="checkbox"
                checked={selectedPromos.bestSeller}
                onChange={() => handlePromoCheckboxChange('bestSeller')}
              />
              <span className="promo-dot best"></span>
              <span>Bestsellers (4.5★+)</span>
            </label>

            <label className="sidebar-checkbox-row">
              <input
                type="checkbox"
                checked={selectedPromos.onSale}
                onChange={() => handlePromoCheckboxChange('onSale')}
              />
              <span className="promo-dot sale"></span>
              <span>Discounted Items</span>
            </label>
          </div>

          {/* Clear Filters Button */}
          {activeFilterCount > 0 && (
            <button className="clear-all-filters-btn" onClick={clearAllFilters}>
              Reset All Filters ({activeFilterCount})
            </button>
          )}
        </aside>

        {/* Main Content Area */}
        <main className="shop-main">
          {/* Top Bar: Search, Results count & Sort */}
          <div className="shop-topbar">
            <div className="shop-results-info">
              <span>Showing <strong>{sortedProducts.length}</strong> products</span>

              {/* Active Filter Pills */}
              {activeFilterCount > 0 && (
                <div className="active-filter-pills">
                  {searchQuery && (
                    <span className="filter-pill">
                      Search: "{searchQuery}"
                      <button onClick={() => setSearchQuery('')}>&times;</button>
                    </span>
                  )}
                  {selectedCategory !== 'All' && (
                    <span className="filter-pill">
                      Category: {selectedCategory}
                      <button onClick={() => setSelectedCategory('All')}>&times;</button>
                    </span>
                  )}
                  {priceMax < 2000 && (
                    <span className="filter-pill">
                      Max Price: {renderPrice(priceMax)}
                      <button onClick={() => setPriceMax(2000)}>&times;</button>
                    </span>
                  )}
                  {selectedRatings.map((r) => (
                    <span key={r} className="filter-pill">
                      {r}★ &amp; up
                      <button onClick={() => handleRatingCheckboxChange(r)}>&times;</button>
                    </span>
                  ))}
                  {selectedPromos.inStock && (
                    <span className="filter-pill">
                      In Stock
                      <button onClick={() => handlePromoCheckboxChange('inStock')}>&times;</button>
                    </span>
                  )}
                  {selectedPromos.flagship && (
                    <span className="filter-pill">
                      Flagship
                      <button onClick={() => handlePromoCheckboxChange('flagship')}>&times;</button>
                    </span>
                  )}
                  {selectedPromos.bestSeller && (
                    <span className="filter-pill">
                      Bestseller
                      <button onClick={() => handlePromoCheckboxChange('bestSeller')}>&times;</button>
                    </span>
                  )}
                  {selectedPromos.onSale && (
                    <span className="filter-pill">
                      On Sale
                      <button onClick={() => handlePromoCheckboxChange('onSale')}>&times;</button>
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Search Input Bar inside shop */}
            <div className="shop-search-bar" style={{ minWidth: '220px', flex: '1', maxWidth: '320px' }}>
              <input
                type="text"
                placeholder="Filter products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button className="shop-search-clear" onClick={() => setSearchQuery('')}>&times;</button>
              )}
            </div>

            {/* Sorting Dropdown */}
            <div className="shop-sort-wrap">
              <span className="sort-label">Sort by:</span>
              <select
                className="shop-sort-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="default">Default / Featured</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="rating">Highest Rated</option>
                <option value="newest">Newest Arrivals</option>
              </select>
            </div>
          </div>

          {/* Product Grid State Handling */}
          {loading ? (
            <div className="shop-loading">
              <div className="shop-spinner"></div>
              <span>Loading products catalog...</span>
            </div>
          ) : sortedProducts.length === 0 ? (
            <div className="shop-empty">
              <div className="shop-empty-icon">🌱</div>
              <h3>No products found</h3>
              <p>We couldn't find any products matching your selected filters or search query.</p>
              <button className="btn-reset-filter" onClick={clearAllFilters}>Clear All Filters</button>
            </div>
          ) : (
            <div className="shop-grid">
              {paginatedProducts.map((prod) => {
                const curPrice = parseFloat(prod.starting_price || prod.price || 0);
                const compPrice = parseFloat(prod.compare_at_price || 0);
                const hasDiscount = compPrice > curPrice;
                const discountPercent = hasDiscount ? Math.round(((compPrice - curPrice) / compPrice) * 100) : 0;
                const isWishlisted = wishlistItems.some(item => item.id === prod.id);
                const isFlagship = prod.slug?.includes('super-novel') || prod.title?.toLowerCase().includes('super novel');
                const isBestseller = parseFloat(prod.rating || 4.0) >= 4.5;
                const imageSrc = (prod.images && prod.images[0]) || '/products/bio-orbi-super-novel.png';

                return (
                  <div key={prod.id} className="shop-product-card">
                    {/* Card Badges */}
                    <div className="card-badges">
                      {hasDiscount && <span className="badge-discount">{discountPercent}% OFF</span>}
                      {isFlagship && <span className="badge-new">Flagship</span>}
                      {isBestseller && !isFlagship && <span className="badge-bestseller">Top Rated</span>}
                    </div>

                    {/* Wishlist Button */}
                    <button
                      className="card-wishlist-btn"
                      onClick={(e) => handleWishlistToggle(prod, e)}
                      title={isWishlisted ? 'Remove from Wishlist' : 'Add to Wishlist'}
                      aria-label="Wishlist"
                    >
                      <SVGIcon name={isWishlisted ? 'heart-filled' : 'heart'} size={16} fill={isWishlisted ? '#e53935' : 'none'} stroke={isWishlisted ? '#e53935' : '#555'} />
                    </button>

                    {/* Image Area */}
                    <div className="card-image-wrap" onClick={() => handleProductClickInternal(prod)}>
                      <img
                        src={imageSrc}
                        alt={prod.title}
                        onError={(e) => { e.target.src = '/products/bio-orbi-super-novel.png'; }}
                      />
                    </div>

                    {/* Information Area */}
                    <div className="card-info">
                      <span className="card-category">{prod.category_name || prod.category || 'Bio Fertilizer'}</span>

                      <div
                        className="card-title"
                        title={prod.title}
                        onClick={() => handleProductClickInternal(prod)}
                      >
                        {prod.title}
                      </div>

                      {/* Ratings */}
                      <div className="card-rating-row">
                        <div className="star-rating">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <span key={star} className={`star ${star <= Math.round(parseFloat(prod.rating || 4.0)) ? 'filled' : ''}`}>★</span>
                          ))}
                        </div>
                        <span className="card-rating-num">({prod.rating || '4.5'})</span>
                      </div>

                      {/* Price Row */}
                      <div className="card-price-row">
                        <span className="card-price">{renderPrice(curPrice)}</span>
                        {hasDiscount && (
                          <span className="card-compare-price">{renderPrice(compPrice)}</span>
                        )}
                      </div>

                      {/* Action Button */}
                      <button
                        className="card-buy-gem-btn"
                        onClick={() => handleProductClickInternal(prod)}
                      >
                        View Product Details
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination Bar */}
          {totalPages > 1 && (
            <div className="shop-pagination">
              <button
                className={`page-btn ${currentPage === 1 ? 'disabled' : ''}`}
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                &laquo; Prev
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                <button
                  key={pageNum}
                  className={`page-btn ${currentPage === pageNum ? 'active' : ''}`}
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </button>
              ))}

              <button
                className={`page-btn ${currentPage === totalPages ? 'disabled' : ''}`}
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
              >
                Next &raquo;
              </button>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
